import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, Settings as SettingsIcon, Shield } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useAdmin } from '../context/AdminContext';
import { branding } from '../config/branding';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [siteLogo, setSiteLogo] = useState(null);
  const location = useLocation();
  const { cartItems, setIsCartOpen } = useCart();
  const { currentTheme } = useTheme();
  const { isAdminAuthenticated } = useAdmin();

  useEffect(() => {
    // Load site branding from backend
    const loadLogo = () => {
      axios.get(`${BACKEND_URL}/api/branding/settings?t=${Date.now()}`)
        .then(response => {
          console.log('Branding response:', response.data);
          if (response.data.logo_url) {
            const logoUrl = `${BACKEND_URL}${response.data.logo_url}?t=${Date.now()}`;
            console.log('Setting logo URL:', logoUrl);
            setSiteLogo(logoUrl);
          }
        })
        .catch(error => {
          console.log('Using default logo, error:', error);
        });
    };
    
    loadLogo();
    
    // Reload logo every 30 seconds to catch updates
    const interval = setInterval(loadLogo, 30000);
    return () => clearInterval(interval);
  }, []);

  const navLinks = [
    { path: '/home', label: 'Home' },
    { path: '/beats', label: 'Beat Store' },
    { path: '/soundkits', label: 'Sound Kits' },
    { path: '/discography', label: 'Discography' },
    { path: '/mix-master', label: 'Mix & Master' },
    { path: '/collaborate', label: 'Collaborate' },
    { path: '/merch', label: 'Merch' },
    { path: '/contact', label: 'Contact' }
  ];

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="/logos/tha-advocate-logo.png" 
              alt="Tha Advocate Beats"
              className="h-8 md:h-10 max-w-[180px] md:max-w-[220px] object-contain transition-all duration-300 group-hover:scale-105"
              onError={(e) => {
                // Fallback to Marz symbol if full logo fails
                e.target.src = '/logos/marz-symbol.png';
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  location.pathname === link.path
                    ? 'text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                style={location.pathname === link.path ? { color: currentTheme.primary } : {}}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {/* Admin */}
            {isAdminAuthenticated && (
              <Link to="/admin/dashboard">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-gray-400 hover:text-white"
                  title="Admin Panel"
                >
                  <Shield className="h-5 w-5" />
                </Button>
              </Link>
            )}

            {/* Settings */}
            <Link to="/settings">
              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                <SettingsIcon className="h-5 w-5" />
              </Button>
            </Link>

            {/* Cart */}
            <Button 
              variant="ghost" 
              size="icon"
              className="relative text-gray-400 hover:text-white"
              onClick={() => setIsCartOpen(true)}
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center text-black font-bold"
                      style={{ backgroundColor: currentTheme.primary }}>
                  {cartCount}
                </span>
              )}
            </Button>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-gray-400 hover:text-white"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-black/98 border-t border-white/10">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  location.pathname === link.path
                    ? 'text-white bg-white/5'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                style={location.pathname === link.path ? { color: currentTheme.primary } : {}}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/settings"
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                location.pathname === '/settings'
                  ? 'text-white bg-white/5'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              style={location.pathname === '/settings' ? { color: currentTheme.primary } : {}}
            >
              Settings
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;